import {ORIGINAL_HOUSINGS} from './bezelSheet.js'

export function originalPlateRegions(){
  return ORIGINAL_HOUSINGS.map(h=>({id:h.id,x:h.crop[0]+h.crop[2]*h.plate[0],y:h.crop[1]+h.crop[3]*h.plate[1],w:h.crop[2]*h.plate[2],h:h.crop[3]*h.plate[3],shape:h.shape==='ellipse'?'ellipse':'rect'}))
}
export function selectionRect(start,end){
  const clamp=n=>Math.max(0,Math.min(1,n))
  const x=Math.min(clamp(start.x),clamp(end.x)),y=Math.min(clamp(start.y),clamp(end.y))
  return {x,y,w:Math.abs(clamp(end.x)-clamp(start.x)),h:Math.abs(clamp(end.y)-clamp(start.y)),shape:'ellipse'}
}
export function squarePlacement(width,height,size=2048){
  const scale=Math.min(size/width,size/height)
  return {size,x:(size-width*scale)/2,y:(size-height*scale)/2,w:width*scale,h:height*scale}
}
export function assertAlignedResult(width,height){
  if(Math.min(width,height)<512||Math.abs(width/height-1)>.015)throw Error('Use the full square image from ChatGPT. A cropped or landscape return cannot align with the bezel mask.')
}
export function copyMaskedPixels(original,edited,mask){
  if(original.length!==edited.length||mask.length!==original.length)throw Error('The source, render and mask dimensions must match.')
  const out=new Uint8ClampedArray(original)
  for(let i=0;i<out.length;i+=4){
    if(mask[i]>127&&mask[i+1]>127&&mask[i+2]>127&&mask[i+3]>127){
      out[i]=edited[i];out[i+1]=edited[i+1];out[i+2]=edited[i+2];out[i+3]=edited[i+3]
    }
  }
  return out
}
export function bezelEditPrompt({team,sourceName,regionCount,metal,stones,notes}={}){
  return [
    'EDIT THE ATTACHED SOURCE IMAGE NOW. Return one square image, matching the source canvas exactly.',
    `This is a Southern Recognition bezel-logo replacement for ${team||'the customer'}. The source is ${sourceName||'the selected SR artwork'}.`,
    'ATTACHMENTS: 01-source.png is the actual artwork. 02-edit-mask.png is an aligned white-on-black mask; only white areas may change. 03-customer-logo.png is the exact logo to use.',
    `Replace the existing center mark in all ${regionCount} white mask areas with the supplied customer logo, rendered as dimensional, jeweler-quality metal relief or gem work. Match each housing's lighting, perspective and finish. Preserve the logo silhouette, internal features and proportions. Do not paste a flat sticker or invent an alternate mark.`,
    'Keep every bezel outline, surrounding stone, shank, pedestal, watermark, headline, footer, original color and background pixel outside the white areas in place. Do not move, resize, crop, rearrange or redesign the rings. Keep the square canvas and its black padding unchanged.',
    'Do not add lettering, mottos, dates or decorations. Any lettering already in the source remains unchanged.',
    metal?`Requested center treatment: ${metal}.`:'Match the existing center metal treatment.',
    stones?`Requested center stone treatment: ${stones}. Apply only inside the mask.`:'',
    notes?`Customer brief: ${notes}`:'',
    'Return the edited image itself, not instructions or analysis.',
  ].filter(Boolean).join('\n\n')
}

export const loadBezelImage=url=>new Promise((resolve,reject)=>{
  const image=new Image();image.crossOrigin='anonymous';image.onload=()=>resolve(image)
  image.onerror=()=>reject(Error('The source image could not be read. Export its PNG from Canva and attach that file.'))
  image.src=url
})
export const canvasBlob=canvas=>new Promise((resolve,reject)=>canvas.toBlob(b=>b?resolve(b):reject(Error('The image could not be exported.')),'image/png'))
export function drawMask(canvas,regions){
  const ctx=canvas.getContext('2d');ctx.fillStyle='#000';ctx.fillRect(0,0,canvas.width,canvas.height);ctx.fillStyle='#fff'
  for(const r of regions){ctx.beginPath();if(r.shape==='ellipse')ctx.ellipse((r.x+r.w/2)*canvas.width,(r.y+r.h/2)*canvas.height,r.w*canvas.width/2,r.h*canvas.height/2,0,0,Math.PI*2);else ctx.rect(r.x*canvas.width,r.y*canvas.height,r.w*canvas.width,r.h*canvas.height);ctx.fill()}
}
export async function prepareBezelReferences(image,regions){
  if(!regions.length)throw Error('Select the center logo area on at least one bezel.')
  const source=document.createElement('canvas'),mask=document.createElement('canvas'),rawMask=document.createElement('canvas')
  const placement=squarePlacement(image.width,image.height)
  source.width=source.height=mask.width=mask.height=placement.size
  const ctx=source.getContext('2d');ctx.fillStyle='#000';ctx.fillRect(0,0,source.width,source.height);ctx.drawImage(image,placement.x,placement.y,placement.w,placement.h)
  rawMask.width=image.width;rawMask.height=image.height;drawMask(rawMask,regions)
  const mx=mask.getContext('2d');mx.fillStyle='#000';mx.fillRect(0,0,mask.width,mask.height);mx.drawImage(rawMask,placement.x,placement.y,placement.w,placement.h)
  return {source:await canvasBlob(source),mask:await canvasBlob(mask),placement}
}
export async function protectHousingPixels({blob,source,regions}){
  const result=await createImageBitmap(blob)
  try{
    assertAlignedResult(result.width,result.height)
    const {x,y,w,h,size}=squarePlacement(source.width,source.height)
    const canvas=document.createElement('canvas'),patch=document.createElement('canvas'),mask=document.createElement('canvas')
    for(const c of [canvas,patch,mask]){c.width=source.width;c.height=source.height}
    const ctx=canvas.getContext('2d',{willReadFrequently:true}),px=patch.getContext('2d',{willReadFrequently:true})
    ctx.drawImage(source,0,0);px.drawImage(result,x*result.width/size,y*result.height/size,w*result.width/size,h*result.height/size,0,0,source.width,source.height)
    drawMask(mask,regions)
    const original=ctx.getImageData(0,0,canvas.width,canvas.height)
    original.data.set(copyMaskedPixels(original.data,px.getImageData(0,0,canvas.width,canvas.height).data,mask.getContext('2d').getImageData(0,0,canvas.width,canvas.height).data))
    ctx.putImageData(original,0,0)
    return await canvasBlob(canvas)
  }finally{result.close()}
}
